import Header from "@/components/header/header";
import TextButton from "@/components/textButton/TextButton";
import { translate } from "@/config/translator";
import { formatDateFromLang, getFileFAIcon } from "@/config/utils";
import {
  Button,
  Popover,
  PopoverSurface,
  PopoverTrigger,
  Table,
  TableBody,
  TableCell,
  TableHeader,
  TableHeaderCell,
  TableRow,
  Text,
} from "@fluentui/react-components";
import { faFile, faFolder } from "@fortawesome/pro-light-svg-icons";
import { faEllipsis } from "@fortawesome/pro-regular-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { filesize } from "filesize";
import { formatPathUrl } from "./submittedDocumentsUtils";
import { TopBarMobile } from "@/components/topBarMobile/topBarMobile";
import PspBreadcrumb from "@/components/pspBreadcrumb/PspBreadcrumb";
// eslint-disable-next-line max-len
import { FullScreenPleaseSelectMandateNotice } from "@/components/fullScreenPleaseSelectMandateNotice/FullScreenPleaseSelectMandateNotice";
import { useSubmittedDocuments } from "./submittedDocuments/useSubmittedDocuments";
import Skeleton from "react-loading-skeleton";
import { File, Folder } from "@/api/openapi";
import FilePreviewDialog from "@/components/filePreviewDialog/filePreviewDialog";

export default function SubmittedDocuments() {
  const {
    selectedMandateId,
    isTablet,
    decodedPath,
    columns,
    currentStructure,
    navigate,
    onDownload,
    lang,
    fileActionOpenPath,
    setFileActionOpenPath,
    onOpenPreview,
    currentFilePreview,
    setCurrentFilePreview,
  } = useSubmittedDocuments();

  if (!selectedMandateId) {
    return <FullScreenPleaseSelectMandateNotice />;
  }

  if (isTablet)
    return (
      <div className="p-10">
        {/* HEADER */}
        <Header text="files" />
        {/* BREADCRUMB */}
        <PspBreadcrumb
          currentPath={decodedPath ?? ""}
          pathPrefix="submitted-documents"
        />
        <Table className="table-fixed">
          <colgroup>
            {columns.map(({ className }) => (
              <col className={className} />
            ))}
          </colgroup>
          <TableHeader>
            <TableRow>
              {columns.map(({ key, label }) => (
                <TableHeaderCell key={`users-table-header-${key}`}>
                  {label}
                </TableHeaderCell>
              ))}
            </TableRow>
          </TableHeader>

          {/* TABLE BODY */}
          <TableBody>
            {(currentStructure && "children" in currentStructure
              ? currentStructure.children
              : (Array.from({ length: 20 }) as undefined[])
            ).map((child: Folder | File | undefined, index) => (
              <TableRow
                className={index % 2 === 0 ? "bg-white" : "bg-canvas-secondary"}
                key={`uploads-webapp-table-row-${child?.path ?? index}`}
              >
                {/* FILETYPE ICON */}
                <TableCell>
                  {child ? (
                    <FontAwesomeIcon
                      size="xl"
                      className="text-font-secondary"
                      icon={
                        child.type === "folder"
                          ? faFolder
                          : "mime" in child
                            ? getFileFAIcon(child?.mime)
                            : faFile
                      }
                    />
                  ) : (
                    <Skeleton circle width={30} className="aspect-square" />
                  )}
                </TableCell>

                {/* FILE NAME */}
                <TableCell>
                  <div className="break-all line-clamp-2">
                    <Text weight="semibold">{child?.name ?? <Skeleton />}</Text>
                  </div>
                </TableCell>

                {/* LAST EDIT DATE */}
                <TableCell>
                  <Text>
                    {child ? (
                      formatDateFromLang(new Date(child.lastEdit), lang)
                    ) : (
                      <Skeleton />
                    )}
                  </Text>
                </TableCell>

                {/* FILE SIZE */}
                <TableCell>
                  <Text>
                    {child ? (
                      "size" in child && typeof child.size === "number" ? (
                        filesize(child.size)
                      ) : (
                        "-"
                      )
                    ) : (
                      <Skeleton />
                    )}
                  </Text>
                </TableCell>

                {/* ACTION */}
                <TableCell>
                  {child?.type === "folder" ? (
                    <TextButton
                      onClick={() =>
                        child ? navigate(formatPathUrl(child.path)) : {}
                      }
                    >
                      {child ? (
                        translate("open", lang)
                      ) : (
                        <Skeleton width={100} />
                      )}
                    </TextButton>
                  ) : (
                    <Popover
                      onOpenChange={(_, data) =>
                        data.open && child
                          ? setFileActionOpenPath(child.path)
                          : setFileActionOpenPath(null)
                      }
                      open={fileActionOpenPath === child?.path}
                    >
                      <PopoverTrigger disableButtonEnhancement>
                        <FontAwesomeIcon
                          icon={faEllipsis}
                          className="text-font-secondary text-3xl cursor-pointer"
                        />
                      </PopoverTrigger>

                      <PopoverSurface>
                        <div className="flex flex-col gap-2 w-50">
                          <h3 className="font-bold text-lg">
                            {translate("action", lang)}
                          </h3>
                          <Button
                            appearance="primary"
                            disabled={!child}
                            onClick={() =>
                              child ? onDownload(child.path) : {}
                            }
                          >
                            {translate("download", lang)}
                          </Button>
                          <Button
                            onClick={() =>
                              child ? onOpenPreview(child.path) : {}
                            }
                          >
                            {translate("preview", lang)}
                          </Button>
                        </div>
                      </PopoverSurface>
                    </Popover>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
        <FilePreviewDialog
          isOpen={!!currentFilePreview}
          onClose={() => setCurrentFilePreview(null)}
          onDownload={onDownload}
          file={currentFilePreview}
        />
      </div>
    );

  return (
    <>
      <TopBarMobile header="submittedDocuments" />
      <div className="p-6"></div>
    </>
  );
}
